import React from 'react';
import { Calendar, Plus, ChevronDown, ChevronRight, Trash2, X } from 'lucide-react';

interface Session {
  id: number;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  instructor: string;
  deliveryMethod: string;
  locationRoom: string;
  virtualMeetingLink: string;
}

interface Offering {
  id: number;
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  timezone: string;
  totalSeats: number;
  enableWaitlist: boolean;
}

interface ILTCourseProps {
  // Offerings state
  offerings: Offering[];
  showOfferingForm: boolean;
  expandedOffering: number | null;
  offeringName: string;
  offeringLocation: string;
  offeringStartDate: string;
  offeringEndDate: string;
  offeringTimezone: string;
  offeringSeats: string;
  enableWaitlist: boolean;

  // Offerings handlers
  onShowOfferingFormToggle: () => void;
  onExpandedOfferingToggle: (id: number | null) => void;
  onOfferingNameChange: (value: string) => void;
  onOfferingLocationChange: (value: string) => void;
  onOfferingStartDateChange: (value: string) => void;
  onOfferingEndDateChange: (value: string) => void;
  onOfferingTimezoneChange: (value: string) => void;
  onOfferingSeatsChange: (value: string) => void;
  onEnableWaitlistChange: (value: boolean) => void;
  onAddOffering: () => void;
  onDeleteOffering: (id: number) => void;

  // Sessions state
  sessions: Session[];
  showSessionForm: boolean;
  sessionDate: string;
  sessionStartTime: string;
  sessionEndTime: string;
  sessionInstructor: string;
  sessionDeliveryMethod: string;
  sessionLocationRoom: string;
  sessionVirtualLink: string;

  // Sessions handlers
  onShowSessionFormToggle: () => void;
  onSessionDateChange: (value: string) => void;
  onSessionStartTimeChange: (value: string) => void;
  onSessionEndTimeChange: (value: string) => void;
  onSessionInstructorChange: (value: string) => void;
  onSessionDeliveryMethodChange: (value: string) => void;
  onSessionLocationRoomChange: (value: string) => void;
  onSessionVirtualLinkChange: (value: string) => void;
  onAddSession: () => void;
  onDeleteSession: (id: number) => void;
}

const ILTCourse: React.FC<ILTCourseProps> = ({
  offerings,
  showOfferingForm,
  expandedOffering,
  offeringName,
  offeringLocation,
  offeringStartDate,
  offeringEndDate,
  offeringTimezone,
  offeringSeats,
  enableWaitlist,
  onShowOfferingFormToggle,
  onExpandedOfferingToggle,
  onOfferingNameChange,
  onOfferingLocationChange,
  onOfferingStartDateChange,
  onOfferingEndDateChange,
  onOfferingTimezoneChange,
  onOfferingSeatsChange,
  onEnableWaitlistChange,
  onAddOffering,
  onDeleteOffering,
  sessions,
  showSessionForm,
  sessionDate,
  sessionStartTime,
  sessionEndTime,
  sessionInstructor,
  sessionDeliveryMethod,
  sessionLocationRoom,
  sessionVirtualLink,
  onShowSessionFormToggle,
  onSessionDateChange,
  onSessionStartTimeChange,
  onSessionEndTimeChange,
  onSessionInstructorChange,
  onSessionDeliveryMethodChange,
  onSessionLocationRoomChange,
  onSessionVirtualLinkChange,
  onAddSession,
  onDeleteSession,
}) => {
  return (
    <>
      {/* Offerings Section */}
      <div className="trainer-coursemanagement-form-section">
        <div className="trainer-coursemanagement-section-header">
          <div className="trainer-coursemanagement-section-icon trainer-coursemanagement-orange">
            <Calendar size={24} color="white" />
          </div>
          <div>
            <h3>Offerings & Sessions</h3>
          </div>
          <button
            className="trainer-coursemanagement-add-offering-btn"
            onClick={onShowOfferingFormToggle}
          >
            <Plus size={16} /> Add Offering
          </button>
        </div>

        {showOfferingForm && (
          <div className="trainer-coursemanagement-offering-form">
            <div className="trainer-coursemanagement-form-row">
              <div className="trainer-coursemanagement-form-group">
                <label>Offering Name</label>
                <input
                  type="text"
                  placeholder="Offering 1"
                  value={offeringName}
                  onChange={(e) => onOfferingNameChange(e.target.value)}
                  className="trainer-coursemanagement-form-input"
                />
              </div>
              <div className="trainer-coursemanagement-form-group">
                <label>Location</label>
                <select
                  value={offeringLocation}
                  onChange={(e) => onOfferingLocationChange(e.target.value)}
                  className="trainer-coursemanagement-form-select"
                >
                  <option value="">Select location</option>
                  <option value="Ahmedabad">Ahmedabad</option>
                  <option value="Mumbai">Mumbai</option>
                  <option value="Delhi">Delhi</option>
                </select>
              </div>
            </div>

            <div className="trainer-coursemanagement-form-row">
              <div className="trainer-coursemanagement-form-group">
                <label>Start Date</label>
                <input
                  type="text"
                  placeholder="dd-mm-yyyy"
                  value={offeringStartDate}
                  onChange={(e) => onOfferingStartDateChange(e.target.value)}
                  className="trainer-coursemanagement-form-input"
                />
              </div>
              <div className="trainer-coursemanagement-form-group">
                <label>End Date</label>
                <input
                  type="text"
                  placeholder="dd-mm-yyyy"
                  value={offeringEndDate}
                  onChange={(e) => onOfferingEndDateChange(e.target.value)}
                  className="trainer-coursemanagement-form-input"
                />
              </div>
            </div>

            <div className="trainer-coursemanagement-form-row">
              <div className="trainer-coursemanagement-form-group">
                <label>Timezone</label>
                <select
                  value={offeringTimezone}
                  onChange={(e) => onOfferingTimezoneChange(e.target.value)}
                  className="trainer-coursemanagement-form-select"
                >
                  <option value="IST">IST</option>
                  <option value="EST">EST</option>
                  <option value="PST">PST</option>
                </select>
              </div>
              <div className="trainer-coursemanagement-form-group">
                <label>Total Seats</label>
                <input
                  type="number"
                  placeholder="30"
                  value={offeringSeats}
                  onChange={(e) => onOfferingSeatsChange(e.target.value)}
                  className="trainer-coursemanagement-form-input"
                />
              </div>
            </div>

            <div className="trainer-coursemanagement-form-group">
              <label className="trainer-coursemanagement-checkbox-label">
                <input
                  type="checkbox"
                  checked={enableWaitlist}
                  onChange={(e) => onEnableWaitlistChange(e.target.checked)}
                />
                <span>Enable Waitlist</span>
              </label>
            </div>

            <div className="trainer-coursemanagement-form-actions">
              <button
                className="trainer-coursemanagement-btn-secondary"
                onClick={onShowOfferingFormToggle}
              >
                Cancel
              </button>
              <button className="trainer-coursemanagement-btn-primary" onClick={onAddOffering}>
                Save Offering
              </button>
            </div>
          </div>
        )}

        {offerings.length === 0 && !showOfferingForm && (
          <div className="trainer-coursemanagement-empty-state">
            <div className="trainer-coursemanagement-empty-icon">
              <Calendar size={48} />
            </div>
            <p className="trainer-coursemanagement-empty-title">No sessions added yet</p>
            <p className="trainer-coursemanagement-empty-subtitle">Click "Add Offering" to create new</p>
          </div>
        )}

        {offerings.map((offering) => (
          <div key={offering.id} className="trainer-coursemanagement-offering-item">
            <div
              className="trainer-coursemanagement-offering-header"
              onClick={() => onExpandedOfferingToggle(expandedOffering === offering.id ? null : offering.id)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {expandedOffering === offering.id ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                <div>
                  <h4>{offering.name}</h4>
                  <p>{offering.location}</p>
                </div>
              </div>
              <button
                className="trainer-coursemanagement-icon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteOffering(offering.id);
                }}
              >
                <Trash2 size={18} />
              </button>
            </div>

            {expandedOffering === offering.id && (
              <div className="trainer-coursemanagement-offering-expanded">
                <div className="trainer-coursemanagement-offering-details-grid">
                  <div className="trainer-coursemanagement-form-group">
                    <label>Offering Name</label>
                    <input type="text" value={offering.name} className="trainer-coursemanagement-form-input" readOnly />
                  </div>
                  <div className="trainer-coursemanagement-form-group">
                    <label>Location</label>
                    <input type="text" value={offering.location} className="trainer-coursemanagement-form-input" readOnly />
                  </div>
                  <div className="trainer-coursemanagement-form-group">
                    <label>Start Date</label>
                    <input type="text" value={offering.startDate} className="trainer-coursemanagement-form-input" readOnly />
                  </div>
                  <div className="trainer-coursemanagement-form-group">
                    <label>End Date</label>
                    <input type="text" value={offering.endDate} className="trainer-coursemanagement-form-input" readOnly />
                  </div>
                  <div className="trainer-coursemanagement-form-group">
                    <label>Timezone</label>
                    <input type="text" value={offering.timezone} className="trainer-coursemanagement-form-input" readOnly />
                  </div>
                  <div className="trainer-coursemanagement-form-group">
                    <label>Total Seats</label>
                    <input type="number" value={offering.totalSeats} className="trainer-coursemanagement-form-input" readOnly />
                  </div>
                </div>

                <div className="trainer-coursemanagement-form-group">
                  <label className="trainer-coursemanagement-checkbox-label">
                    <input type="checkbox" checked={offering.enableWaitlist} disabled />
                    <span>Enable Waitlist</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Sessions Section - Separate from Offerings */}
      <div className="trainer-coursemanagement-sessions-container">
        <div className="trainer-coursemanagement-section-header">
          <div className="trainer-coursemanagement-section-icon trainer-coursemanagement-blue">
            <Calendar size={24} color="white" />
          </div>
          <div>
            <h3>Sessions ({sessions.length})</h3>
          </div>
          <button
            className="trainer-coursemanagement-add-offering-btn"
            onClick={onShowSessionFormToggle}
          >
            <Plus size={16} /> Add Session
          </button>
        </div>

        {showSessionForm && (
          <div className="trainer-coursemanagement-session-form">
            <div className="trainer-coursemanagement-form-row trainer-coursemanagement-three-col">
              <div className="trainer-coursemanagement-form-group">
                <label>Date</label>
                <input
                  type="text"
                  placeholder="dd-mm-yyyy"
                  value={sessionDate}
                  onChange={(e) => onSessionDateChange(e.target.value)}
                  className="trainer-coursemanagement-form-input"
                />
              </div>
              <div className="trainer-coursemanagement-form-group">
                <label>Start Time</label>
                <input
                  type="text"
                  placeholder="Select Time"
                  value={sessionStartTime}
                  onChange={(e) => onSessionStartTimeChange(e.target.value)}
                  className="trainer-coursemanagement-form-input"
                />
              </div>
              <div className="trainer-coursemanagement-form-group">
                <label>End Time</label>
                <input
                  type="text"
                  placeholder="Select Time"
                  value={sessionEndTime}
                  onChange={(e) => onSessionEndTimeChange(e.target.value)}
                  className="trainer-coursemanagement-form-input"
                />
              </div>
            </div>

            <div className="trainer-coursemanagement-form-row trainer-coursemanagement-three-col">
              <div className="trainer-coursemanagement-form-group">
                <label>Instructor</label>
                <input
                  type="text"
                  placeholder="Search & Select"
                  value={sessionInstructor}
                  onChange={(e) => onSessionInstructorChange(e.target.value)}
                  className="trainer-coursemanagement-form-input"
                />
              </div>
              <div className="trainer-coursemanagement-form-group">
                <label>Delivery Method</label>
                <select
                  value={sessionDeliveryMethod}
                  onChange={(e) => onSessionDeliveryMethodChange(e.target.value)}
                  className="trainer-coursemanagement-form-select"
                >
                  <option value="Virtual">Virtual</option>
                  <option value="In-Person">In-Person</option>
                  <option value="Hybrid">Hybrid</option>
                </select>
              </div>
              <div className="trainer-coursemanagement-form-group">
                <label>Location/Room</label>
                <input
                  type="text"
                  placeholder="Search & Select"
                  value={sessionLocationRoom}
                  onChange={(e) => onSessionLocationRoomChange(e.target.value)}
                  className="trainer-coursemanagement-form-input"
                />
              </div>
            </div>

            {sessionDeliveryMethod === 'Virtual' && (
              <div className="trainer-coursemanagement-form-group">
                <label>Virtual Meeting Link</label>
                <input
                  type="text"
                  placeholder="https://zoom.us/j/..."
                  value={sessionVirtualLink}
                  onChange={(e) => onSessionVirtualLinkChange(e.target.value)}
                  className="trainer-coursemanagement-form-input"
                />
              </div>
            )}

            <div className="trainer-coursemanagement-form-actions">
              <button
                className="trainer-coursemanagement-btn-secondary"
                onClick={onShowSessionFormToggle}
              >
                Cancel
              </button>
              <button className="trainer-coursemanagement-btn-primary" onClick={onAddSession}>
                Save Session
              </button>
            </div>
          </div>
        )}

        {sessions.length === 0 && !showSessionForm && (
          <div className="trainer-coursemanagement-empty-state">
            <div className="trainer-coursemanagement-empty-icon">
              <Calendar size={48} />
            </div>
            <p className="trainer-coursemanagement-empty-subtitle">
              No sessions added yet. Click "Add Session" to get started.
            </p>
          </div>
        )}

        {sessions.map((session) => (
          <div key={session.id} className="trainer-coursemanagement-session-card">
            <div className="trainer-coursemanagement-session-card-header">
              <h4>{session.name}</h4>
              <button
                className="trainer-coursemanagement-icon-btn"
                onClick={() => onDeleteSession(session.id)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="trainer-coursemanagement-session-details">
              <div className="trainer-coursemanagement-session-detail">
                <span className="trainer-coursemanagement-session-label">Date:</span>
                <span>{session.date}</span>
              </div>
              <div className="trainer-coursemanagement-session-detail">
                <span className="trainer-coursemanagement-session-label">Start Time:</span>
                <span>{session.startTime}</span>
              </div>
              <div className="trainer-coursemanagement-session-detail">
                <span className="trainer-coursemanagement-session-label">End Time:</span>
                <span>{session.endTime}</span>
              </div>
              <div className="trainer-coursemanagement-session-detail">
                <span className="trainer-coursemanagement-session-label">Instructor:</span>
                <span>{session.instructor}</span>
              </div>
              <div className="trainer-coursemanagement-session-detail">
                <span className="trainer-coursemanagement-session-label">Delivery Method:</span>
                <span>{session.deliveryMethod}</span>
              </div>
              <div className="trainer-coursemanagement-session-detail">
                <span className="trainer-coursemanagement-session-label">Location/Room:</span>
                <span>{session.locationRoom}</span>
              </div>
              {session.virtualMeetingLink && (
                <div className="trainer-coursemanagement-session-detail">
                  <span className="trainer-coursemanagement-session-label">Virtual Link:</span>
                  <a
                    href={session.virtualMeetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="trainer-coursemanagement-session-link"
                  >
                    {session.virtualMeetingLink}
                  </a>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default ILTCourse;
